import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  GraduationCap, BookOpen, Anchor, CalendarDays, TrendingUp,
  Gauge, ClipboardList, ChevronRight, Lock, CheckCircle2,
  Award, Layers, Shield, Star, Bot, Sparkles, FileText,
  Loader2, Send, BarChart3, MessageSquare, Plus, X,
  CalendarHeart, Route, DollarSign, Rocket, Flame, Clock, Brain
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ICONS = { GraduationCap, BookOpen, Anchor, CalendarDays, TrendingUp, Gauge, ClipboardList, CalendarHeart, Route, DollarSign, Rocket };
const COLORS = {
  indigo: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', border: 'border-indigo-500/20' },
  emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  purple: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
  amber: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  cyan: { bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/20' },
  rose: { bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/20' },
  blue: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
};

const LEVELS_FE = [
  { level: 1, min_xp: 0 }, { level: 2, min_xp: 50 }, { level: 3, min_xp: 150 },
  { level: 4, min_xp: 350 }, { level: 5, min_xp: 600 }, { level: 6, min_xp: 1000 },
  { level: 7, min_xp: 1500 }, { level: 8, min_xp: 2200 }, { level: 9, min_xp: 3000 },
  { level: 10, min_xp: 4000 },
];

const BADGE_ICONS = [
  { id: 'first_lesson', name: 'First Step', desc: 'Complete your first lesson', emoji: '\u26A1' },
  { id: 'ten_lessons', name: 'Dedicated Learner', desc: 'Complete 10 lessons', emoji: '\uD83D\uDCD6' },
  { id: 'twenty_five_lessons', name: 'Knowledge Seeker', desc: 'Complete 25 lessons', emoji: '\uD83E\uDDE0' },
  { id: 'fifty_lessons', name: 'Scholar Elite', desc: 'Complete 50 lessons', emoji: '\uD83C\uDF93' },
  { id: 'first_course', name: 'Course Complete', desc: 'Finish your first course', emoji: '\u2705' },
  { id: 'five_courses', name: 'Multi-Track Mind', desc: 'Complete 5 courses', emoji: '\uD83D\uDCDA' },
  { id: 'ten_courses', name: 'Course Conqueror', desc: 'Complete 10 courses', emoji: '\uD83C\uDFC6' },
  { id: 'first_cert', name: 'Certified Pro', desc: 'Earn your first certification', emoji: '\uD83C\uDF96\uFE0F' },
  { id: 'three_certs', name: 'Triple Threat', desc: 'Earn 3 certifications', emoji: '\uD83D\uDEE1\uFE0F' },
  { id: 'five_certs', name: 'Certification Master', desc: 'Earn 5 certifications', emoji: '\uD83D\uDC51' },
  { id: 'perfect_score', name: 'Perfect Score', desc: 'Get 100% on a quiz', emoji: '\uD83C\uDFAF' },
  { id: 'five_perfect', name: 'Flawless', desc: 'Get 5 perfect scores', emoji: '\u2B50' },
  { id: 'streak_3', name: 'On a Roll', desc: '3-day learning streak', emoji: '\uD83D\uDD25' },
  { id: 'streak_7', name: 'Week Warrior', desc: '7-day learning streak', emoji: '\uD83D\uDD25' },
  { id: 'streak_30', name: 'Monthly Master', desc: '30-day streak', emoji: '\uD83D\uDD25' },
  { id: 'speed_learner', name: 'Speed Learner', desc: 'Course in a single day', emoji: '\uD83D\uDE80' },
];

export default function UniversityDashboard() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isPublic = !token;
  const isVenueOwner = user?.role === 'venue_owner';
  const [tracks, setTracks] = useState([]);
  const [certStatus, setCertStatus] = useState(null);
  const [ceStatus, setCeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('courses');
  const [purchases, setPurchases] = useState([]);
  const [purchaseLoading, setPurchaseLoading] = useState(false);

  // AI Content Engine state
  const [generating, setGenerating] = useState(null);
  const [aiHistory, setAiHistory] = useState([]);
  const [aiSettings, setAiSettings] = useState({ auto_generate: false });
  const [monthlyHistory, setMonthlyHistory] = useState([]);

  // Survey state
  const [surveys, setSurveys] = useState([]);
  const [showSurveyForm, setShowSurveyForm] = useState(false);
  const [activeSurvey, setActiveSurvey] = useState(null);
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [surveyResults, setSurveyResults] = useState(null);

  // Leaderboard & Gamification state
  const [leaderboard, setLeaderboard] = useState([]);
  const [myGamification, setMyGamification] = useState(null);
  const [myRank, setMyRank] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const load = async () => {
      try {
        if (isPublic) {
          // Public view: only show advanced training tracks
          const res = await axios.get(`${API}/university/public/tracks`);
          setTracks(res.data.tracks);
        } else {
          const [tracksRes, certRes, ceRes, purchasesRes] = await Promise.all([
            axios.get(`${API}/university/tracks`, { headers }),
            axios.get(`${API}/university/certification-status`, { headers }),
            axios.get(`${API}/university/ce-status`, { headers }),
            axios.get(`${API}/university/my-purchases`, { headers }),
          ]);
          setTracks(tracksRes.data.tracks);
          setCertStatus(certRes.data);
          setCeStatus(ceRes.data);
          setPurchases(purchasesRes.data.purchases || []);
          
          // Check for purchase success
          const params = new URLSearchParams(window.location.search);
          if (params.get('purchase') === 'success') {
            const trackId = params.get('track');
            if (trackId) {
              await axios.post(`${API}/university/purchase/confirm`, { track_id: trackId }, { headers });
              toast.success('Course purchased! You now have full access.');
              window.history.replaceState({}, '', '/university');
              // Reload purchases
              const pRes = await axios.get(`${API}/university/my-purchases`, { headers });
              setPurchases(pRes.data.purchases || []);
            }
          }
        }
      } catch (err) { 
        if (!isPublic) toast.error('Failed to load university data'); 
      }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  const handlePurchaseCourse = async (trackId) => {
    if (!token) {
      toast.info('Please sign in to purchase this course');
      navigate('/login?redirect=/training');
      return;
    }
    setPurchaseLoading(true);
    try {
      const res = await axios.post(`${API}/university/purchase/${trackId}`, {}, { headers });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setPurchaseLoading(false);
    }
  };

  const hasPurchased = (trackId) => {
    if (isAdmin) return true;
    return purchases.some(p => p.track_id === trackId);
  };

  useEffect(() => {
    if (tab === 'ai' && isAdmin) loadAiData();
    if (tab === 'surveys') loadSurveys();
    if (tab === 'leaderboard' && !isPublic) loadLeaderboard();
  }, [tab]);

  const loadAiData = async () => {
    try {
      const [histRes, settRes, monthlyRes] = await Promise.all([
        axios.get(`${API}/university/ai/content-history`, { headers }),
        axios.get(`${API}/university/ai/settings`, { headers }),
        axios.get(`${API}/university/monthly-content-history`, { headers }),
      ]);
      setAiHistory(histRes.data.history);
      setAiSettings(settRes.data);
      setMonthlyHistory(monthlyRes.data.history || []);
    } catch (err) { console.error(err); }
  };

  const loadSurveys = async () => {
    try {
      const res = await axios.get(`${API}/university/surveys`, { headers });
      setSurveys(res.data.surveys);
    } catch (err) { console.error(err); }
  };

  const loadLeaderboard = async () => {
    setLeaderboardLoading(true);
    try {
      const [lbRes, gamRes] = await Promise.all([
        axios.get(`${API}/university/leaderboard`, { headers }),
        axios.get(`${API}/university/my-gamification`, { headers }),
      ]);
      setLeaderboard(lbRes.data.leaderboard || []);
      setMyRank(lbRes.data.my_rank);
      setMyGamification(gamRes.data);
    } catch (err) { console.error('Failed to load leaderboard:', err); }
    finally { setLeaderboardLoading(false); }
  };

  const generateContent = async (type) => {
    setGenerating(type);
    try {
      const res = await axios.post(`${API}/university/ai/generate-content`, { type }, { headers });
      toast.success(res.data.message);
      loadAiData();
      // Reload tracks to show new AI content
      const tr = await axios.get(`${API}/university/tracks`, { headers });
      setTracks(tr.data.tracks);
    } catch (err) { toast.error(err.response?.data?.detail || 'Content generation failed'); }
    finally { setGenerating(null); }
  };

  const toggleAutoGenerate = async () => {
    try {
      const res = await axios.put(`${API}/university/ai/settings`, { auto_generate: !aiSettings.auto_generate }, { headers });
      setAiSettings(res.data);
      toast.success(`Auto-generate ${res.data.auto_generate ? 'enabled' : 'disabled'}`);
    } catch (err) { toast.error('Failed to update settings'); }
  };

  const submitSurveyResponse = async (surveyId) => {
    try {
      await axios.post(`${API}/university/surveys/${surveyId}/respond`, { answers: surveyAnswers }, { headers });
      toast.success('Survey response submitted!');
      setSurveyAnswers({});
      setActiveSurvey(null);
      loadSurveys();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
  };

  const viewResults = async (surveyId) => {
    try {
      const res = await axios.get(`${API}/university/surveys/${surveyId}/results`, { headers });
      setSurveyResults(res.data);
    } catch (err) { toast.error('Failed to load results'); }
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-pulse text-muted-foreground text-sm">Loading Eventism University...</div>
    </div>
  );

  const mandatoryTracks = tracks.filter(t => t.is_mandatory);
  const allMandatoryComplete = mandatoryTracks.length > 0 && mandatoryTracks.every(t => t.completed_courses === t.total_courses && t.total_courses > 0);
  const ceTracks = allMandatoryComplete ? tracks.filter(t => !t.is_mandatory && t.ce_eligible) : [];
  const paidTracks = tracks.filter(t => t.is_paid);
  const otherAdvancedTracks = allMandatoryComplete ? tracks.filter(t => !t.is_mandatory && !t.ce_eligible && !t.is_paid) : [];
  // For public view, show all non-paid tracks as advanced training
  const publicAdvancedTracks = isPublic ? tracks.filter(t => !t.is_paid) : [];
  const totalCourses = tracks.reduce((s, t) => s + t.total_courses, 0);
  const completedCourses = tracks.reduce((s, t) => s + t.completed_courses, 0);
  const overallPct = totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0;

  const TABS = [
    { id: 'courses', label: 'Training', icon: BookOpen },
    { id: 'ce', label: 'Continuing Ed', icon: Flame },
    ...(!isPublic ? [{ id: 'leaderboard', label: 'Leaderboard', icon: TrendingUp }] : []),
    { id: 'surveys', label: 'Surveys', icon: MessageSquare },
  ];
  if (isAdmin) TABS.push({ id: 'ai', label: 'AI Engine', icon: Bot });

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="university-dashboard">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 via-slate-950 to-emerald-600/10" />
        <div className="relative max-w-7xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 rounded-xl bg-indigo-500/20">
              <GraduationCap className="h-7 w-7 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground tracking-tight">Eventism University</h1>
              <p className="text-muted-foreground text-base mt-1">
                {isPublic ? 'Professional Event Training & Certification' : 'Training = Performance = Profits'}
              </p>
            </div>
            {!isPublic && (isAdmin || user?.email?.toLowerCase() === 'monice@eventism.com' || user?.email?.toLowerCase() === 'dondrae@eventism.com') && (
              <Button size="sm" variant="outline" className="ml-auto border-purple-500/30 text-purple-400 hover:bg-purple-500/10" onClick={() => navigate('/university/executive')} data-testid="exec-center-btn">
                <Brain className="h-4 w-4 mr-1" /> Executive Command Center
              </Button>
            )}
          </div>

          {/* Stats - only for authenticated users */}
          {!isPublic && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                {[
                  { val: tracks.length, label: 'Training Tracks' },
                  { val: totalCourses, label: 'Total Courses' },
                  { val: completedCourses, label: 'Completed', cls: 'text-emerald-400' },
                  { val: certStatus?.certifications?.length || 0, label: 'Certifications' },
                ].map((s, i) => (
                  <div key={i} className="p-3 rounded-xl bg-card/80 border border-border">
                    <p className={`text-2xl font-bold ${s.cls || 'text-foreground'}`}>{s.val}</p>
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Progress */}
              <div className="mt-5 p-3 rounded-xl bg-muted/60 border border-border">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-medium text-muted-foreground">Overall Progress</p>
                  <p className="text-sm font-bold text-foreground">{overallPct}%</p>
                </div>
                <Progress value={overallPct} className="h-2" />
              </div>
            </>
          )}

          {/* Public CTA */}
          {isPublic && (
            <div className="mt-6 p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
              <p className="text-sm text-muted-foreground">Browse our advanced training library. <strong>Sign in</strong> for full access to all courses, progress tracking, and certifications.</p>
              <Button size="sm" className="mt-3" onClick={() => navigate('/login?redirect=/training')} data-testid="public-signin-cta">
                Sign In for Full Access
              </Button>
            </div>
          )}

          {/* Free for Sales Agents Banner */}
          {!isPublic && user?.role === 'sales_agent' && (
            <div className="mt-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-start gap-3" data-testid="free-courses-banner">
              <GraduationCap className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-emerald-300">All Courses Are Free for Sales Agents</p>
                <p className="text-xs text-muted-foreground">As a member of the Eventism Sales Team, every class, course, and certification in Eventism University is 100% free for you. Invest in your skills and close more deals.</p>
              </div>
            </div>
          )}

          {/* Cert Banner */}
          {!isPublic && certStatus && !certStatus.certified && (
            <div className="mt-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-start gap-3" data-testid="certification-banner">
              <Lock className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-300">Certification Required</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Complete <strong>{certStatus.missing_tracks.map(t => t.name).join(' & ')}</strong> to unlock the Sales Agent Dashboard.
                </p>
              </div>
            </div>
          )}
          {!isPublic && certStatus?.certified && (
            <div className="mt-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center gap-3" data-testid="certified-banner">
              <Award className="h-5 w-5 text-emerald-400 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-emerald-300">EU Certified</p>
                <p className="text-xs text-muted-foreground">All mandatory tracks completed. Sales Agent Dashboard unlocked.</p>
              </div>
              {user?.role === 'sales_agent' && (
                <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-black shrink-0" onClick={() => navigate('/sales')} data-testid="go-to-sales-dashboard-btn">
                  <Rocket className="h-4 w-4 mr-1" /> Go to Sales Dashboard
                </Button>
              )}
            </div>
          )}

          {/* CE Status Banner */}
          {!isPublic && ceStatus && (
            <div className={`mt-3 p-3 rounded-xl flex items-start gap-3 ${
              ceStatus.requirement_met
                ? 'bg-emerald-500/10 border border-emerald-500/20'
                : 'bg-amber-500/10 border border-amber-500/20'
            }`} data-testid="ce-status-banner">
              <Flame className={`h-5 w-5 mt-0.5 shrink-0 ${ceStatus.requirement_met ? 'text-emerald-400' : 'text-amber-400'}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-semibold ${ceStatus.requirement_met ? 'text-emerald-300' : 'text-amber-300'}`}>
                    Continuing Education — {ceStatus.current_month}
                  </p>
                  {ceStatus.streak_months > 0 && (
                    <Badge className="bg-indigo-500/20 text-indigo-400 text-[10px]">
                      {ceStatus.streak_months} month streak
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {ceStatus.requirement_met
                    ? `Requirement met! ${ceStatus.courses_completed_this_month.length} CE course(s) completed this month.`
                    : `Complete 1 CE course this month. ${ceStatus.days_remaining} day(s) remaining.`
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation - only for authenticated */}
      {!isPublic && (
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 border-b border-border mb-6">
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    tab === t.id ? 'border-indigo-500 text-indigo-400' : 'border-transparent text-muted-foreground hover:text-muted-foreground'
                  }`}
                  data-testid={`tab-${t.id}`}
                >
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 pb-16">
        {/* ===== COURSES TAB ===== */}
        {(tab === 'courses' || isPublic) && (
          <>
            {/* Required Training - only for authenticated */}
            {!isPublic && mandatoryTracks.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="h-5 w-5 text-amber-400" />
                  <h2 className="text-lg font-extrabold text-foreground">Required Training</h2>
                  <Badge variant="outline" className="text-amber-400 border-amber-500/30 text-[10px]">MANDATORY</Badge>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {mandatoryTracks.map(track => <TrackCard key={track.track_id} track={track} navigate={navigate} />)}
                </div>
              </div>
            )}

            {/* CE-eligible courses section - only for authenticated AND mandatory complete */}
            {!isPublic && ceTracks.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Flame className="h-5 w-5 text-rose-400" />
                  <h2 className="text-lg font-extrabold text-foreground">Continuing Education</h2>
                  <Badge variant="outline" className="text-rose-400 border-rose-500/30 text-[10px]">CE CREDIT</Badge>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {ceTracks.map(track => <TrackCard key={track.track_id} track={track} navigate={navigate} />)}
                </div>
              </div>
            )}

            {otherAdvancedTracks.length > 0 && !isPublic && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="h-5 w-5 text-indigo-400" />
                  <h2 className="text-lg font-extrabold text-foreground">Advanced Training</h2>
                  <Badge variant="outline" className="text-muted-foreground border-border text-[10px]">ELECTIVE</Badge>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {otherAdvancedTracks.map(track => <TrackCard key={track.track_id} track={track} navigate={navigate} />)}
                </div>
              </div>
            )}

            {/* Paid Professional Course - moved to bottom */}
            {paidTracks.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Award className="h-5 w-5 text-amber-400" />
                  <h2 className="text-lg font-extrabold text-foreground">Professional Certification</h2>
                  <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">$200</Badge>
                </div>
                <div className="grid md:grid-cols-1 gap-4">
                  {paidTracks.map(track => (
                    <Card key={track.track_id} className="bg-gradient-to-r from-amber-500/5 to-indigo-500/5 border-amber-500/20 hover:border-amber-500/40 transition-all" data-testid={`paid-track-${track.track_id}`}>
                      <CardContent className="p-5 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <GraduationCap className="h-6 w-6 text-amber-400" />
                              <h3 className="text-lg font-bold text-foreground">{track.name}</h3>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">{track.description}</p>
                            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                              <span>{track.total_courses} course(s)</span>
                              <span>12 modules</span>
                              <span>Industry-standard certification</span>
                            </div>
                          </div>
                          <div className="text-center sm:text-right flex-shrink-0">
                            <p className="text-3xl font-bold text-amber-400">${track.price || 200}</p>
                            <p className="text-xs text-muted-foreground mb-3">one-time payment</p>
                            {hasPurchased(track.track_id) ? (
                              <Button size="sm" onClick={() => navigate(`/university/course/${track.courses?.[0]?.course_id}`)} data-testid="start-paid-course">
                                <BookOpen className="h-4 w-4 mr-1" /> Continue Course
                              </Button>
                            ) : (
                              <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black" onClick={() => handlePurchaseCourse(track.track_id)} disabled={purchaseLoading} data-testid="purchase-course-btn">
                                {purchaseLoading ? 'Processing...' : 'Enroll Now — $200'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Not mandatory complete - show locked message */}
            {!isPublic && !allMandatoryComplete && mandatoryTracks.length > 0 && (
              <div className="p-4 rounded-xl bg-muted/50 border border-border text-center">
                <Lock className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm font-medium text-muted-foreground">Complete all mandatory training to unlock Continuing Education and Advanced courses.</p>
              </div>
            )}

            {/* Public Advanced Training Section */}
            {isPublic && publicAdvancedTracks.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Star className="h-5 w-5 text-indigo-400" />
                  <h2 className="text-lg font-extrabold text-foreground">Free Advanced Training</h2>
                  <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 text-[10px]">FREE</Badge>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {publicAdvancedTracks.map(track => <TrackCard key={track.track_id} track={track} navigate={navigate} />)}
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== CONTINUING EDUCATION TAB ===== */}
        {tab === 'ce' && ceStatus && (
          <div className="space-y-6" data-testid="ce-tab-content">
            {/* CE Status Card */}
            <Card className={`border ${ceStatus.requirement_met ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`}>
              <CardContent className="p-6 text-center">
                <div className={`w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4 ${ceStatus.requirement_met ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                  {ceStatus.requirement_met
                    ? <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                    : <Clock className="h-8 w-8 text-amber-400" />
                  }
                </div>
                <h2 className={`text-2xl font-bold mb-1 ${ceStatus.requirement_met ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {ceStatus.requirement_met ? 'CE Requirement Met!' : 'CE Required'}
                </h2>
                <p className="text-muted-foreground text-sm mb-4">{ceStatus.requirement}</p>
                <div className="flex items-center justify-center gap-6">
                  <div>
                    <p className="text-2xl font-bold text-foreground">{ceStatus.streak_months}</p>
                    <p className="text-[10px] text-muted-foreground">Month Streak</p>
                  </div>
                  <div className="w-px h-8 bg-muted" />
                  <div>
                    <p className="text-2xl font-bold text-foreground">{ceStatus.courses_completed_this_month.length}</p>
                    <p className="text-[10px] text-muted-foreground">Completed This Month</p>
                  </div>
                  <div className="w-px h-8 bg-muted" />
                  <div>
                    <p className="text-2xl font-bold text-foreground">{ceStatus.days_remaining}</p>
                    <p className="text-[10px] text-muted-foreground">Days Left</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CE Course Progress */}
            <div>
              <h3 className="text-base font-bold text-foreground mb-3 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-indigo-400" /> CE-Eligible Courses
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {ceStatus.available_courses.map(course => {
                  const pct = course.total_lessons > 0
                    ? Math.round((course.completed_lessons / course.total_lessons) * 100)
                    : 0;
                  const color = COLORS[course.track_color] || COLORS.indigo;
                  return (
                    <Card key={course.course_id}
                      className={`bg-muted/60 border-border hover:border-border transition-all cursor-pointer ${course.is_complete ? 'ring-1 ring-emerald-500/30' : ''}`}
                      onClick={() => navigate(`/university/course/${course.course_id}`)}
                      data-testid={`ce-course-${course.course_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <Badge className={`${color.bg} ${color.text} text-[10px] mb-1.5`}>{course.track_name}</Badge>
                            <h4 className="text-sm font-semibold text-foreground">{course.name}</h4>
                          </div>
                          {course.is_complete && <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />}
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={pct} className="h-1.5 flex-1" />
                          <span className="text-[10px] text-muted-foreground shrink-0">
                            {course.completed_lessons}/{course.total_lessons}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ===== LEADERBOARD TAB ===== */}
        {tab === 'leaderboard' && !isPublic && (
          <div className="space-y-6" data-testid="leaderboard-tab">
            {leaderboardLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
              </div>
            ) : (
              <>
                {/* My Gamification Profile */}
                {myGamification && (
                  <Card className="border-indigo-500/20 bg-gradient-to-r from-indigo-500/5 via-background to-amber-500/5" data-testid="my-gamification-card">
                    <CardContent className="p-5 sm:p-6">
                      <div className="flex flex-col sm:flex-row gap-6">
                        {/* XP Ring */}
                        <div className="flex flex-col items-center gap-2">
                          <div className="relative w-28 h-28">
                            <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
                              <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted/20" />
                              <circle cx="50" cy="50" r="42" fill="none" strokeWidth="6"
                                className="text-indigo-500"
                                stroke="currentColor"
                                strokeDasharray={`${myGamification.level.next_level_xp ? ((myGamification.xp - (LEVELS_FE.find(l => l.level === myGamification.level.level)?.min_xp || 0)) / (myGamification.level.next_level_xp - (LEVELS_FE.find(l => l.level === myGamification.level.level)?.min_xp || 0))) * 264 : 264} 264`}
                                strokeLinecap="round"
                              />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <span className="text-2xl font-black text-indigo-400">Lv{myGamification.level.level}</span>
                              <span className="text-[10px] text-muted-foreground font-medium">{myGamification.level.name}</span>
                            </div>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-foreground" data-testid="my-xp">{myGamification.xp.toLocaleString()} XP</p>
                            {myGamification.level.next_level_xp && (
                              <p className="text-xs text-muted-foreground">{myGamification.level.next_level_xp - myGamification.xp} XP to {myGamification.level.next_level_name}</p>
                            )}
                          </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-foreground">Your Stats</h3>
                            {myRank && (
                              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-sm px-3" data-testid="my-rank-badge">
                                Rank #{myRank}
                              </Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                            <div className="bg-muted/30 rounded-lg p-3 text-center">
                              <p className="text-xl font-bold text-foreground">{myGamification.stats.lessons_completed}</p>
                              <p className="text-[10px] text-muted-foreground">Lessons</p>
                            </div>
                            <div className="bg-muted/30 rounded-lg p-3 text-center">
                              <p className="text-xl font-bold text-foreground">{myGamification.stats.courses_completed}</p>
                              <p className="text-[10px] text-muted-foreground">Courses</p>
                            </div>
                            <div className="bg-muted/30 rounded-lg p-3 text-center">
                              <p className="text-xl font-bold text-foreground">{myGamification.stats.certifications}</p>
                              <p className="text-[10px] text-muted-foreground">Certifications</p>
                            </div>
                            <div className="bg-muted/30 rounded-lg p-3 text-center">
                              <p className="text-xl font-bold text-amber-400">{myGamification.streak}</p>
                              <p className="text-[10px] text-muted-foreground">Day Streak</p>
                            </div>
                          </div>

                          {/* Badges */}
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground mb-2">
                              Badges ({myGamification.badge_count}/{myGamification.total_badges})
                            </p>
                            <div className="flex flex-wrap gap-2" data-testid="my-badges">
                              {BADGE_ICONS.map(badge => {
                                const earned = myGamification.badges.some(b => b.id === badge.id);
                                return (
                                  <div
                                    key={badge.id}
                                    title={earned ? `${badge.name}: ${badge.desc}` : `Locked: ${badge.desc}`}
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm transition-all ${
                                      earned
                                        ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30'
                                        : 'bg-muted/20 text-muted-foreground/30 border border-transparent'
                                    }`}
                                  >
                                    {badge.emoji}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Leaderboard Table */}
                <Card data-testid="leaderboard-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-amber-400" />
                      Leaderboard
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {leaderboard.length === 0 ? (
                      <div className="text-center py-10 text-muted-foreground">
                        <GraduationCap className="h-10 w-10 mx-auto mb-3 opacity-30" />
                        <p className="font-medium">No learners yet</p>
                        <p className="text-sm">Complete your first lesson to appear on the leaderboard!</p>
                      </div>
                    ) : (
                      <>
                        {/* Top 3 Podium */}
                        {leaderboard.length >= 1 && (
                          <div className="flex items-end justify-center gap-3 sm:gap-6 mb-8 pt-4" data-testid="podium">
                            {/* 2nd place */}
                            {leaderboard[1] && (
                              <div className="flex flex-col items-center">
                                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-400/20 border-2 border-slate-400 flex items-center justify-center text-lg font-bold text-slate-300 overflow-hidden">
                                  {leaderboard[1].picture ? <img src={leaderboard[1].picture} alt="" className="w-full h-full object-cover" /> : leaderboard[1].name?.charAt(0)}
                                </div>
                                <p className="text-xs font-semibold mt-1 text-foreground truncate max-w-[80px]">{leaderboard[1].name?.split(' ')[0]}</p>
                                <p className="text-[10px] text-muted-foreground">{leaderboard[1].xp.toLocaleString()} XP</p>
                                <div className="w-16 sm:w-20 h-16 bg-slate-500/20 rounded-t-lg mt-2 flex items-center justify-center">
                                  <span className="text-xl font-black text-slate-400">2</span>
                                </div>
                              </div>
                            )}
                            {/* 1st place */}
                            <div className="flex flex-col items-center">
                              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-amber-500/20 border-2 border-amber-400 flex items-center justify-center text-xl font-bold text-amber-300 overflow-hidden">
                                {leaderboard[0].picture ? <img src={leaderboard[0].picture} alt="" className="w-full h-full object-cover" /> : leaderboard[0].name?.charAt(0)}
                              </div>
                              <p className="text-sm font-bold mt-1 text-foreground truncate max-w-[90px]">{leaderboard[0].name?.split(' ')[0]}</p>
                              <p className="text-xs text-amber-400 font-semibold">{leaderboard[0].xp.toLocaleString()} XP</p>
                              <div className="w-20 sm:w-24 h-24 bg-amber-500/20 rounded-t-lg mt-2 flex items-center justify-center">
                                <span className="text-3xl font-black text-amber-400">1</span>
                              </div>
                            </div>
                            {/* 3rd place */}
                            {leaderboard[2] && (
                              <div className="flex flex-col items-center">
                                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-orange-700/20 border-2 border-orange-600 flex items-center justify-center text-base font-bold text-orange-400 overflow-hidden">
                                  {leaderboard[2].picture ? <img src={leaderboard[2].picture} alt="" className="w-full h-full object-cover" /> : leaderboard[2].name?.charAt(0)}
                                </div>
                                <p className="text-xs font-semibold mt-1 text-foreground truncate max-w-[80px]">{leaderboard[2].name?.split(' ')[0]}</p>
                                <p className="text-[10px] text-muted-foreground">{leaderboard[2].xp.toLocaleString()} XP</p>
                                <div className="w-14 sm:w-18 h-12 bg-orange-700/20 rounded-t-lg mt-2 flex items-center justify-center">
                                  <span className="text-lg font-black text-orange-500">3</span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Full Rankings Table */}
                        <div className="space-y-1" data-testid="leaderboard-list">
                          {leaderboard.map((entry) => (
                            <div
                              key={entry.user_id}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                                entry.user_id === user?.user_id
                                  ? 'bg-indigo-500/10 border border-indigo-500/20'
                                  : 'hover:bg-muted/30'
                              }`}
                              data-testid={`leaderboard-entry-${entry.rank}`}
                            >
                              {/* Rank */}
                              <div className="w-8 text-center flex-shrink-0">
                                {entry.rank <= 3 ? (
                                  <span className={`text-lg font-black ${
                                    entry.rank === 1 ? 'text-amber-400' : entry.rank === 2 ? 'text-slate-400' : 'text-orange-500'
                                  }`}>
                                    {entry.rank === 1 ? '\u{1F947}' : entry.rank === 2 ? '\u{1F948}' : '\u{1F949}'}
                                  </span>
                                ) : (
                                  <span className="text-sm font-bold text-muted-foreground">#{entry.rank}</span>
                                )}
                              </div>

                              {/* Avatar */}
                              <div className="w-9 h-9 rounded-full bg-muted/50 flex items-center justify-center text-sm font-bold text-muted-foreground overflow-hidden flex-shrink-0">
                                {entry.picture ? <img src={entry.picture} alt="" className="w-full h-full object-cover" /> : entry.name?.charAt(0)}
                              </div>

                              {/* Name & Level */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-foreground truncate">
                                  {entry.name}
                                  {entry.user_id === user?.user_id && <span className="text-indigo-400 ml-1">(You)</span>}
                                </p>
                                <p className="text-[10px] text-muted-foreground">
                                  Lv{entry.level.level} {entry.level.name}
                                </p>
                              </div>

                              {/* Stats */}
                              <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
                                <span title="Courses">{entry.stats.courses_completed} courses</span>
                                <span title="Certifications">{entry.stats.certifications} certs</span>
                                <span title="Badges">{entry.badge_count} badges</span>
                                {entry.streak > 0 && (
                                  <span className="text-amber-400" title="Streak">{entry.streak}d streak</span>
                                )}
                              </div>

                              {/* XP */}
                              <div className="text-right flex-shrink-0">
                                <p className="text-sm font-bold text-indigo-400">{entry.xp.toLocaleString()}</p>
                                <p className="text-[10px] text-muted-foreground">XP</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Badge Collection */}
                {myGamification && (
                  <Card data-testid="badge-collection-card">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2">
                        <Award className="h-5 w-5 text-indigo-400" />
                        Badge Collection
                        <Badge variant="outline" className="text-muted-foreground ml-1">{myGamification.badge_count}/{myGamification.total_badges}</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {BADGE_ICONS.map(badge => {
                          const earned = myGamification.badges.some(b => b.id === badge.id);
                          return (
                            <div
                              key={badge.id}
                              className={`p-3 rounded-xl border text-center transition-all ${
                                earned
                                  ? 'bg-indigo-500/5 border-indigo-500/20'
                                  : 'bg-muted/10 border-border/50 opacity-40'
                              }`}
                              data-testid={`badge-${badge.id}`}
                            >
                              <div className={`text-2xl mb-1 ${earned ? '' : 'grayscale'}`}>{badge.emoji}</div>
                              <p className="text-xs font-semibold text-foreground">{badge.name}</p>
                              <p className="text-[10px] text-muted-foreground mt-0.5">{badge.desc}</p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* XP Breakdown */}
                <Card data-testid="xp-breakdown-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      <Sparkles className="h-4 w-4 text-amber-400" />
                      How to Earn XP
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      {[
                        { action: 'Complete a lesson', xp: 10, icon: BookOpen },
                        { action: 'Perfect quiz score', xp: 25, icon: Star },
                        { action: 'Complete a course', xp: 50, icon: CheckCircle2 },
                        { action: 'Complete a track', xp: 100, icon: Layers },
                        { action: 'Earn a certification', xp: 200, icon: Award },
                      ].map(item => (
                        <div key={item.action} className="flex items-center gap-2 p-2 rounded-lg bg-muted/20">
                          <item.icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-muted-foreground truncate">{item.action}</p>
                            <p className="text-sm font-bold text-amber-400">+{item.xp} XP</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* ===== SURVEYS TAB ===== */}
        {tab === 'surveys' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-indigo-400" /> Surveys & Feedback
              </h2>
              {isAdmin && (
                <Button size="sm" onClick={() => setShowSurveyForm(true)} className="bg-indigo-500 hover:bg-indigo-600" data-testid="create-survey-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Create Survey
                </Button>
              )}
            </div>

            {/* Survey Creator (Admin) */}
            {showSurveyForm && <SurveyCreator token={token} onClose={() => { setShowSurveyForm(false); loadSurveys(); }} />}

            {/* Survey Results Modal */}
            {surveyResults && (
              <Card className="bg-muted/60 border-border" data-testid="survey-results-card">
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <CardTitle className="text-base text-foreground flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-emerald-400" /> Results: {surveyResults.survey.title}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSurveyResults(null)}><X className="h-4 w-4" /></Button>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-3">{surveyResults.total_responses} responses</p>
                  <div className="space-y-3">
                    {Object.entries(surveyResults.aggregated).map(([qId, data]) => (
                      <div key={qId} className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs font-medium text-muted-foreground mb-2">{data.question}</p>
                        {Object.entries(data.answers).map(([ans, count]) => (
                          <div key={ans} className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                            <div className="flex-1 bg-muted rounded-full h-1.5">
                              <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width: `${(count / surveyResults.total_responses) * 100}%` }} />
                            </div>
                            <span className="shrink-0 w-32 truncate">{ans}</span>
                            <span className="shrink-0 text-foreground font-medium">{count}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Survey List */}
            {surveys.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No active surveys</p>}
            {surveys.map(s => (
              <Card key={s.survey_id} className="bg-muted/60 border-border" data-testid={`survey-card-${s.survey_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-foreground">{s.title}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{s.questions?.length || 0} questions | {s.response_count} responses</p>
                    </div>
                    <div className="flex gap-1.5">
                      {isAdmin && (
                        <Button variant="outline" size="sm" onClick={() => viewResults(s.survey_id)} className="text-xs border-border" data-testid={`view-results-${s.survey_id}`}>
                          <BarChart3 className="h-3 w-3 mr-1" /> Results
                        </Button>
                      )}
                      {s.user_responded ? (
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>
                      ) : (
                        <Button size="sm" onClick={() => { setActiveSurvey(s); setSurveyAnswers({}); }}
                          className="bg-indigo-500 hover:bg-indigo-600 text-xs" data-testid={`take-survey-${s.survey_id}`}>
                          Take Survey
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Inline Survey Form */}
                  {activeSurvey?.survey_id === s.survey_id && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3" data-testid="survey-form">
                      {s.questions.map((q, qi) => (
                        <div key={q.id || qi}>
                          <Label className="text-xs text-muted-foreground">{qi + 1}. {q.question}</Label>
                          {q.type === 'text' ? (
                            <Textarea value={surveyAnswers[q.id] || ''} onChange={e => setSurveyAnswers(p => ({ ...p, [q.id]: e.target.value }))}
                              className="bg-muted border-border mt-1 text-xs" rows={2} placeholder="Your answer..." />
                          ) : (
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {q.options?.map(opt => (
                                <button key={opt} onClick={() => setSurveyAnswers(p => ({ ...p, [q.id]: opt }))}
                                  className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                                    surveyAnswers[q.id] === opt
                                      ? 'bg-indigo-500/20 border-indigo-500/40 text-indigo-300'
                                      : 'bg-muted border-border text-muted-foreground hover:border-border'
                                  }`}
                                  data-testid={`survey-opt-${q.id}-${opt}`}
                                >{opt}</button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => submitSurveyResponse(s.survey_id)} className="bg-emerald-500 hover:bg-emerald-600 text-xs" data-testid="submit-survey-btn">
                          <Send className="h-3 w-3 mr-1" /> Submit
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setActiveSurvey(null)} className="text-xs text-muted-foreground">Cancel</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* ===== AI ENGINE TAB (Admin Only) ===== */}
        {tab === 'ai' && isAdmin && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 mb-2">
              <Bot className="h-5 w-5 text-cyan-400" />
              <h2 className="text-lg font-bold text-foreground">AI Content Engine</h2>
              <Badge className="bg-cyan-500/20 text-cyan-400 text-[10px]">Powered by Gemini</Badge>
            </div>

            {/* Auto-Generate Toggle */}
            <Card className="bg-muted/60 border-border" data-testid="ai-settings-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">Weekly Auto-Generation</p>
                    <p className="text-xs text-muted-foreground">AI generates industry research + platform analysis every Monday</p>
                  </div>
                  <button onClick={toggleAutoGenerate}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${aiSettings.auto_generate ? 'bg-cyan-500' : 'bg-muted'}`}
                    data-testid="ai-auto-toggle"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${aiSettings.auto_generate ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* Generate Buttons */}
            <div className="grid md:grid-cols-3 gap-3">
              {[
                { type: 'weekly_research', label: 'Weekly Industry Intel', desc: 'Trends, competitor moves, sales tactics for your cities', icon: TrendingUp, color: 'amber' },
                { type: 'platform_analysis', label: 'Platform Self-Analysis', desc: 'AI analyzes metrics, errors, opportunities & suggests optimizations', icon: Gauge, color: 'cyan' },
                { type: 'competitor_analysis', label: 'Competitor Deep Dive', desc: 'Battle cards, positioning, win strategies vs each competitor', icon: BarChart3, color: 'rose' },
              ].map(g => {
                const Icon = g.icon;
                const isGen = generating === g.type;
                return (
                  <Card key={g.type} className="bg-muted/60 border-border hover:border-border transition-colors">
                    <CardContent className="p-4">
                      <div className={`p-2 rounded-lg ${COLORS[g.color]?.bg} w-fit mb-2`}>
                        <Icon className={`h-5 w-5 ${COLORS[g.color]?.text}`} />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground">{g.label}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5 mb-3">{g.desc}</p>
                      <Button size="sm" onClick={() => generateContent(g.type)} disabled={!!generating}
                        className="w-full bg-muted hover:bg-accent border border-border text-xs"
                        data-testid={`generate-${g.type}`}
                      >
                        {isGen ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                        {isGen ? 'Generating...' : 'Generate Now'}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Generate Advanced Course Content */}
            <Card className="bg-muted/60 border-border" data-testid="ai-advanced-content-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground flex items-center gap-2">
                      <Flame className="h-4 w-4 text-rose-400" /> CE Course Content Generator
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Generate AI content for all pending lessons in CE-eligible courses (Seasonal Mastery, Customer Journey, Finance, Denver Launch)
                    </p>
                  </div>
                  <Button size="sm" onClick={() => generateContent('advanced_courses')} disabled={!!generating}
                    className="bg-rose-500 hover:bg-rose-600 text-xs"
                    data-testid="generate-advanced-courses"
                  >
                    {generating === 'advanced_courses' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
                    {generating === 'advanced_courses' ? 'Generating...' : 'Generate All CE Content'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Monthly CE Content Generator */}
            <Card className="bg-indigo-500/5 border-indigo-500/20" data-testid="monthly-content-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground flex items-center gap-2">
                      <CalendarDays className="h-4 w-4 text-indigo-400" /> Monthly CE Content
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Each month, generate a fresh topical lesson for every CE course — covering that month's events, trends, and strategies. Auto-runs on startup if not yet generated.
                    </p>
                    {monthlyHistory.length > 0 && (
                      <p className="text-[10px] text-indigo-400 mt-1.5">
                        Latest: {monthlyHistory[0]?.month} ({monthlyHistory[0]?.lessons?.length} lessons generated)
                      </p>
                    )}
                  </div>
                  <Button size="sm" onClick={() => generateContent('monthly_content')} disabled={!!generating}
                    className="bg-indigo-500 hover:bg-indigo-600 text-xs shrink-0"
                    data-testid="generate-monthly-content"
                  >
                    {generating === 'monthly_content' ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CalendarDays className="h-3.5 w-3.5 mr-1.5" />}
                    {generating === 'monthly_content' ? 'Generating...' : 'Generate This Month'}
                  </Button>
                </div>

                {/* Monthly Content History */}
                {monthlyHistory.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-border">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-2">Monthly Editions</p>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {monthlyHistory.map(m => (
                        <div key={m.month} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                          <div>
                            <p className="text-xs font-medium text-foreground">{m.month}</p>
                            <p className="text-[10px] text-muted-foreground">{m.lessons.length} lessons</p>
                          </div>
                          <div className="flex gap-1">
                            {m.lessons.map(l => {
                              const short = l.course_id.replace('course_', '').substring(0, 3).toUpperCase();
                              return (
                                <span key={l.lesson_id} className="px-1.5 py-0.5 rounded text-[8px] bg-indigo-500/10 text-indigo-400 font-mono" title={l.title}>
                                  {short}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Content History */}
            <div>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" /> Generation History
              </h3>
              {aiHistory.length === 0 && <p className="text-muted-foreground text-xs">No AI content generated yet</p>}
              <div className="space-y-2">
                {aiHistory.map(h => (
                  <div key={h.generation_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{h.title}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{h.content_type} | {new Date(h.generated_at).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {h.tags?.map(t => <Badge key={t} variant="outline" className="text-[9px] border-border text-muted-foreground">{t}</Badge>)}
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/university/course/${h.course_id}`)} className="text-xs text-indigo-400">
                        View <ChevronRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TrackCard({ track, navigate }) {
  const colors = COLORS[track.color] || COLORS.indigo;
  const Icon = ICONS[track.icon] || BookOpen;
  const pct = track.total_courses > 0 ? Math.round((track.completed_courses / track.total_courses) * 100) : 0;

  return (
    <Card className="bg-muted/60 border-border hover:border-border transition-all" data-testid={`track-card-${track.track_id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${colors.bg}`}>
              <Icon className={`h-5 w-5 ${colors.text}`} />
            </div>
            <div>
              <CardTitle className="text-lg font-bold text-foreground leading-tight">{track.name}</CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5 font-medium">{track.total_courses} course{track.total_courses !== 1 ? 's' : ''}</p>
            </div>
          </div>
          {track.is_certified ? (
            <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" />Certified</Badge>
          ) : track.is_mandatory ? (
            <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Required</Badge>
          ) : track.ce_eligible ? (
            <Badge className="bg-rose-500/20 text-rose-400 text-[10px]">CE Credit</Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4 leading-relaxed line-clamp-2">{track.description}</p>
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-xs text-muted-foreground font-medium">{track.completed_courses}/{track.total_courses} courses done</span>
            <span className="text-xs font-bold text-foreground">{pct}%</span>
          </div>
          <Progress value={pct} className="h-2" />
        </div>
        <div className="space-y-2">
          {track.courses?.map(c => (
            <button key={c.course_id} onClick={() => navigate(`/university/course/${c.course_id}`)}
              className="w-full flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors text-left"
              data-testid={`course-btn-${c.course_id}`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                {c.is_complete ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> : <Layers className="h-4 w-4 text-muted-foreground shrink-0" />}
                <span className="text-sm font-medium text-muted-foreground truncate">{c.name}</span>
                {c.ai_generated && <Badge className="bg-cyan-500/20 text-cyan-400 text-[8px] px-1">AI</Badge>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-muted-foreground font-medium">{c.completed_lessons}/{c.total_lessons}</span>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SurveyCreator({ token, onClose }) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [questions, setQuestions] = useState([{ id: 'q1', question: '', type: 'choice', options: [''] }]);
  const [saving, setSaving] = useState(false);

  const addQuestion = () => {
    setQuestions(p => [...p, { id: `q${p.length + 1}`, question: '', type: 'choice', options: [''] }]);
  };

  const updateQ = (idx, field, val) => {
    setQuestions(p => p.map((q, i) => i === idx ? { ...q, [field]: val } : q));
  };

  const addOption = (idx) => {
    setQuestions(p => p.map((q, i) => i === idx ? { ...q, options: [...(q.options || []), ''] } : q));
  };

  const updateOption = (qIdx, oIdx, val) => {
    setQuestions(p => p.map((q, i) => i === qIdx ? { ...q, options: q.options.map((o, j) => j === oIdx ? val : o) } : q));
  };

  const save = async () => {
    if (!title) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/university/surveys`, { title, description: desc, questions }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Survey created!');
      onClose();
    } catch (err) { toast.error('Failed to create survey'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="bg-muted/60 border-indigo-500/30" data-testid="survey-creator">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">New Survey</h3>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div>
          <Label className="text-xs">Title</Label>
          <Input value={title} onChange={e => setTitle(e.target.value)} className="bg-muted border-border text-xs" data-testid="survey-title-input" />
        </div>
        <div>
          <Label className="text-xs">Description</Label>
          <Textarea value={desc} onChange={e => setDesc(e.target.value)} className="bg-muted border-border text-xs" rows={2} />
        </div>
        {questions.map((q, qi) => (
          <div key={qi} className="p-3 rounded-lg bg-muted/50 space-y-2">
            <div className="flex gap-2">
              <Input value={q.question} onChange={e => updateQ(qi, 'question', e.target.value)} placeholder={`Question ${qi + 1}`}
                className="bg-muted border-border text-xs flex-1" />
              <select value={q.type} onChange={e => updateQ(qi, 'type', e.target.value)}
                className="bg-muted border border-border rounded-md text-xs text-muted-foreground px-2">
                <option value="choice">Choice</option>
                <option value="rating">Rating</option>
                <option value="text">Text</option>
              </select>
            </div>
            {q.type !== 'text' && (
              <div className="space-y-1">
                {q.options?.map((opt, oi) => (
                  <Input key={oi} value={opt} onChange={e => updateOption(qi, oi, e.target.value)}
                    placeholder={`Option ${oi + 1}`} className="bg-muted border-border text-xs" />
                ))}
                <Button variant="ghost" size="sm" onClick={() => addOption(qi)} className="text-[10px] text-muted-foreground">
                  <Plus className="h-3 w-3 mr-1" /> Add Option
                </Button>
              </div>
            )}
          </div>
        ))}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={addQuestion} className="text-xs border-border text-muted-foreground">
            <Plus className="h-3 w-3 mr-1" /> Add Question
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600 text-xs ml-auto" data-testid="save-survey-btn">
            {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
            Create Survey
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
